import { Id } from '@api/index';
import ErrorBoundary from '@components/ErrorBoundry';
import MultipleVehiclesSelectedState from '@components/MultipleVehiclesSelectedState';
import SectionTitle from '@components/SectionTitle';
import SimpleErrorState from '@components/SimpleErrorState';
import EmptyOpConState from '@features/opcon/EmptyOpConState';
import { Spinner } from '@rio-cloud/rio-uikit';
import { useContext } from 'react';

import { DateRange } from '../../types';
import BatteryProvider, { BatteryContext, Status } from './BatteryProvider';
import ChargeGraph from './ChargeGraph';
import { StateOfChargeDataPoint } from './types';

const StateOfCharge = ({
    stateOfCharges,
    hasStateOfChargeRequestFailed,
    hasStateOfChargeInfo,
    dateRange,
}: {
    stateOfCharges: StateOfChargeDataPoint[] | undefined;
    hasStateOfChargeRequestFailed: boolean;
    hasStateOfChargeInfo: boolean;
    dateRange: DateRange;
}) => {
    if (hasStateOfChargeRequestFailed) {
        return <SimpleErrorState fullWidth className="padding-top-10 padding-bottom-20" data-test="soc-error-state" />;
    }
    if (!hasStateOfChargeInfo || stateOfCharges === undefined) {
        return <EmptyOpConState className="padding-top-10 padding-bottom-0" />;
    }

    return <ChargeGraph stateOfCharges={stateOfCharges} dateRange={dateRange} />;
};

export const Battery = ({ dateRange, vehicleIds }: { dateRange: DateRange; vehicleIds: Id[] }) => {
    const { stateOfCharge } = useContext(BatteryContext);

    const hasMultipleVehiclesInSelection = vehicleIds.length > 1;
    if (hasMultipleVehiclesInSelection) {
        return <MultipleVehiclesSelectedState messageKey="battery.multipleVehiclesSelectedMessage" />;
    }

    if (stateOfCharge.status === Status.LOADING) {
        return <Spinner />;
    }

    const hasStateOfChargeRequestFailed = stateOfCharge.status === Status.FAILED;

    if (hasStateOfChargeRequestFailed) {
        return <SimpleErrorState fullWidth className="padding-bottom-20" data-test="battery-error-state" />;
    }

    const hasStateOfChargeInfo = stateOfCharge.status === Status.SUCCESS && (stateOfCharge.data || []).length > 0;

    if (!hasStateOfChargeRequestFailed && !hasStateOfChargeInfo) {
        return <EmptyOpConState className="padding-bottom-20" />;
    }

    return (
        <div data-test="battery">
            <ErrorBoundary>
                <SectionTitle title="stateOfChargeMonitor" icon="rioglyph-error-sign" />
                <StateOfCharge
                    stateOfCharges={stateOfCharge.data}
                    hasStateOfChargeInfo={hasStateOfChargeInfo}
                    hasStateOfChargeRequestFailed={hasStateOfChargeRequestFailed}
                    dateRange={dateRange}
                />
            </ErrorBoundary>
        </div>
    );
};

const SelfLoadingBattery = ({
    dateRange,
    driverIds,
    vehicleIds,
}: {
    dateRange: DateRange;
    driverIds: (Id | null)[];
    vehicleIds: Id[];
}) => (
    <BatteryProvider dateRange={dateRange} driverIds={driverIds} vehicleIds={vehicleIds}>
        <Battery dateRange={dateRange} vehicleIds={vehicleIds} />
    </BatteryProvider>
);

export default SelfLoadingBattery;
